import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { getRouter } from '@/core/router/routerBridge';
import { localSource } from '@/datasource/local/localSource';
import { setUnauthorizedHandler } from '@/datasource/network/services/backendService';
import { aksaraAdminAuthUsecase } from '@/projects/aksara/usecases/adminAuthUsecase';
import { silsilahAdminAuthUsecase } from '@/projects/silsilah/usecases/adminAuthUsecase';
import type { AuthSession, ProjectId } from '@/models/model.type';

const PROJECTS: ProjectId[] = ['aksara', 'silsilah'];

export const useAuthStore = defineStore('auth', () => {
  const sessions = ref<Partial<Record<ProjectId, AuthSession>>>(localSource.getSessions());
  const project = ref<ProjectId | null>(localSource.getProject());
  const isLoading = ref(false);
  const isBootstrapping = ref(false);

  const activeSession = computed(() => (project.value ? sessions.value[project.value] : undefined));
  const user = computed(() => activeSession.value?.user ?? null);
  const token = computed(() => activeSession.value?.token ?? null);
  const isAuthenticated = computed(() => !!activeSession.value);

  function persistSessions(): void {
    localSource.setSessions(sessions.value);
    if (project.value) localSource.setProject(project.value);
    else localSource.removeProject();
  }

  function hasSession(target: ProjectId): boolean {
    return !!sessions.value[target];
  }

  function setActiveProject(target: ProjectId): boolean {
    if (!hasSession(target)) return false;
    project.value = target;
    persistSessions();
    return true;
  }

  function removeSession(target: ProjectId): void {
    const next = { ...sessions.value };
    delete next[target];
    sessions.value = next;
    if (project.value === target) {
      project.value = PROJECTS.find((candidate) => !!next[candidate]) ?? null;
    }
    persistSessions();
  }

  function clearSession(): void {
    sessions.value = {};
    project.value = null;
    localSource.clearAuth();
  }

  async function login(selectedProject: ProjectId, googleToken: string): Promise<void> {
    isLoading.value = true;
    try {
      const session =
        selectedProject === 'aksara'
          ? await aksaraAdminAuthUsecase.login(googleToken)
          : await silsilahAdminAuthUsecase.login(googleToken);

      sessions.value = {
        ...sessions.value,
        [selectedProject]: session,
      };
      project.value = selectedProject;
      persistSessions();
    } finally {
      isLoading.value = false;
    }
  }

  async function logout(): Promise<void> {
    const currentProject = project.value;
    if (currentProject) removeSession(currentProject);
    const router = getRouter();
    if (project.value) {
      await router.push(`/${project.value}/dashboard`);
    } else {
      await router.push('/login');
    }
  }

  async function logoutAll(): Promise<void> {
    clearSession();
    await getRouter().push('/login');
  }

  async function bootstrap(): Promise<void> {
    isBootstrapping.value = true;
    try {
      for (const target of PROJECTS) {
        const session = sessions.value[target];
        if (!session) continue;
        try {
          const profile =
            target === 'aksara'
              ? await aksaraAdminAuthUsecase.profile(session.token)
              : await silsilahAdminAuthUsecase.profile(session.token);
          sessions.value = {
            ...sessions.value,
            [target]: { ...session, user: profile },
          };
        } catch {
          removeSession(target);
        }
      }
      if (!project.value || !hasSession(project.value)) {
        project.value = PROJECTS.find(hasSession) ?? null;
      }
      persistSessions();
    } finally {
      isBootstrapping.value = false;
    }
  }

  function handleUnauthorized(): void {
    const currentProject = project.value;
    if (currentProject) removeSession(currentProject);
    const router = getRouter();
    void router.replace({
      path: '/login',
      query: {
        ...(currentProject ? { project: currentProject } : {}),
        redirect: router.currentRoute.value.fullPath,
      },
    });
  }

  function initAuth(): () => void {
    setUnauthorizedHandler(handleUnauthorized);
    const listener = (): void => handleUnauthorized();
    window.addEventListener('auth:unauthorized', listener);
    return () => window.removeEventListener('auth:unauthorized', listener);
  }

  return {
    sessions,
    project,
    user,
    token,
    isAuthenticated,
    isLoading,
    isBootstrapping,
    hasSession,
    setActiveProject,
    removeSession,
    login,
    logout,
    logoutAll,
    bootstrap,
    clearSession,
    initAuth,
  };
});
